import React from 'react';

import {FloatingFocusManager, FloatingOverlay} from '@floating-ui/react';

import OverlayPortal from '../../OverlayPortal';

import {NonVirtualizedAutocompleteListBox} from './NonVirtualizedAutocompleteListBox';
import {VirtualizedAutocompleteListBox} from './VirtualizedAutocompleteListBox';
import type {AutocompleteListBoxProps} from './_types';
import {useSearchableListContext} from '../../SearchableList/useSearchableList';

const AutocompleteListBoxSwitcher = React.forwardRef<
  HTMLDivElement,
  Omit<AutocompleteListBoxProps, 'shouldPreventBodyScroll'>
>(({isVirtualized, maxHeight, scrollContainerRef, ...props}, ref) => {
  return isVirtualized && maxHeight !== undefined && scrollContainerRef ? (
    <VirtualizedAutocompleteListBox
      ref={ref}
      {...props}
      maxHeight={maxHeight}
      scrollContainerRef={scrollContainerRef}
    />
  ) : (
    <NonVirtualizedAutocompleteListBox ref={ref} {...props} />
  );
});

export const AutocompleteListBox = React.forwardRef<
  HTMLDivElement,
  AutocompleteListBoxProps
>(
  (
    {
      isVirtualized,
      maxHeight,
      scrollContainerRef,
      shouldPreventBodyScroll,
      ...props
    },
    ref
  ) => {
    const {context, isOpen} = useSearchableListContext();

    return (
      <>
        {isOpen ? (
          <OverlayPortal>
            <FloatingOverlay lockScroll={shouldPreventBodyScroll} />
            <FloatingFocusManager
              context={context}
              initialFocus={-1}
              returnFocus={false}
              visuallyHiddenDismiss
            >
              <AutocompleteListBoxSwitcher
                isVirtualized={isVirtualized}
                maxHeight={maxHeight}
                ref={ref}
                scrollContainerRef={scrollContainerRef}
                {...props}
              />
            </FloatingFocusManager>
          </OverlayPortal>
        ) : null}
      </>
    );
  }
);
